import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { Link, useSearchParams } from "react-router-dom";
import { FETCH_MY_SYSTEM_TASKS, PAGE_SIZE } from "./graphql";

const SystemTasksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams({ offset: "0" });
  const offset = parseInt(searchParams.get("offset") || "0", 10) || 0;
  const listVariables = {
    includeCompleted: true,
    options: {
      limit: PAGE_SIZE,
      offset,
    },
  };
  const { loading, error, data } = useQuery(FETCH_MY_SYSTEM_TASKS, {
    variables: listVariables,
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const allTasks = data?.mySystemTasks?.results || [];
  const openTasks = allTasks.filter((task) => !task.completedAt);
  const completedTasks = allTasks.filter((task) => task.completedAt);
  const total = data?.mySystemTasks?.total || 0;

  return (
    <Page title="System Tasks" className="h-screen">
      <div className="p-6 h-full flex flex-col">
        <div className="card p-0 flex-1 overflow-auto flex flex-col">
          <div className="flex-1 overflow-auto">
            {allTasks.length === 0 ? (
              <div className="p-6 opacity-60">No tasks found.</div>
            ) : (
              <div className="p-4 md:p-6 space-y-6">
                <div>
                  <div className="text-xs opacity-70 mb-2">Open Tasks ({openTasks.length})</div>
                  {openTasks.length === 0 ? (
                    <div className="opacity-60 text-sm">No open tasks.</div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {openTasks.map((task) => (
                        <div key={task.id} className="py-3">
                          <Link
                            className="link font-semibold"
                            to={`/tools/system-tasks/${task.id}`}
                          >
                            {task.title || `Task #${task.id}`}
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {completedTasks.length > 0 ? (
                  <details>
                    <summary className="cursor-pointer opacity-70">
                      Completed Tasks ({completedTasks.length})
                    </summary>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700 mt-2">
                      {completedTasks.map((task) => (
                        <div key={task.id} className="py-3">
                          <Link
                            className="link font-semibold"
                            to={`/tools/system-tasks/${task.id}`}
                          >
                            {task.title || `Task #${task.id}`}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            )}
          </div>
          <div className="p-4 md:p-6 flex justify-end items-center space-x-2 opacity-70">
            {offset > 0 ? (
              <Button
                prev
                onClick={() =>
                  setSearchParams({
                    offset: String(Math.max(0, offset - PAGE_SIZE)),
                  })
                }
              />
            ) : null}
            <div>
              {total === 0 ? 0 : offset + 1}-{Math.min(offset + PAGE_SIZE, total)} /{" "}
              {total}
            </div>
            {total > offset + PAGE_SIZE ? (
              <Button
                next
                onClick={() =>
                  setSearchParams({
                    offset: String(offset + PAGE_SIZE),
                  })
                }
              />
            ) : null}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default SystemTasksPage;
